<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import { Feature, PageName } from '@/api/tracking';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import BasicEmptyFeed from '@/components/common/basic-empty-feed.vue';
import ThemeEmptyFeed from '@/components/common/theme-empty-feed.vue';
import {
  AdpDocument,
  ArticleMarkSource,
  DocType,
  FeedType,
  Theme,
  ThemeFeedType,
} from '@/types';
import { docTypeDisplayNameMap } from '@/utils/adpDocument';
import { useThemeDisabledOption } from '@/utils/composables/useThemeDisabledOption';

interface Props {
  title: string;
  formattedFeedDate?: string;
  displayFeedType: FeedType;
  themeFeedType: ThemeFeedType;
  docType: DocType;
  adpDocuments: AdpDocument[];
  initialFeedCount?: number;
  maxFeedCount?: number;
  theme?: Theme;
  enableToEditTheme?: boolean;
  pageName: PageName;
  feature: Feature;
  markSource?: ArticleMarkSource;
}

const props = withDefaults(defineProps<Props>(), {
  initialFeedCount: 20,
  maxFeedCount: 200,
});

const { adpDocuments } = toRefs(props);

const isShowAllButtonVisible = ref(
  adpDocuments.value.length > props.initialFeedCount,
);
const visibleAdpDocuments = computed(() =>
  adpDocuments.value.slice(
    0,
    isShowAllButtonVisible.value ? props.initialFeedCount : props.maxFeedCount,
  ),
);
const loadAllAdpDocuments = () => (isShowAllButtonVisible.value = false);

const noFeedMessage = computed(
  () => `${docTypeDisplayNameMap[props.docType]}はありませんでした。`,
);

const disabledOption = computed(
  () =>
    (props.theme
      ? useThemeDisabledOption({
          theme: props.theme,
          themeFeedType: props.themeFeedType,
          docType: props.docType,
        })
      : { disabledOption: undefined }
    ).disabledOption,
);

watch(adpDocuments, (newAdpDocuments: AdpDocument[]) => {
  isShowAllButtonVisible.value =
    newAdpDocuments.length > props.initialFeedCount;
});
</script>

<template>
  <div class="document-list">
    <div class="title c-title-bold--m">
      <div class="title-text">{{ title }}</div>
      <div v-if="!disabledOption" class="feed-count c-text-regular--s">
        ・ {{ adpDocuments.length }}件
      </div>
    </div>

    <div class="divider"></div>
    <div
      class="document-card-wrapper"
      v-for="(document, index) in visibleAdpDocuments"
      :key="document.id"
    >
      <AdpDocumentCard
        width="100%"
        :adp-document="document"
        :theme-id="document.theme_id"
        :page-name="pageName"
        :feature="feature"
        :mark-source="markSource"
        :disable-related-articles="false"
      ></AdpDocumentCard>
      <div
        v-if="index !== visibleAdpDocuments.length - 1"
        class="divider"
      ></div>
    </div>

    <div v-if="isShowAllButtonVisible" class="view-more-button__wrapper">
      <div
        class="view-more-button c-text-regular--m c-btn c-btn--auto c-btn--small c-btn--outline"
        @click="() => loadAllAdpDocuments()"
      >
        すべて表示{{ `（${adpDocuments.length - initialFeedCount}）` }}
      </div>
    </div>

    <template v-if="adpDocuments.length === 0">
      <ThemeEmptyFeed
        v-if="theme"
        :display-feed-type="displayFeedType"
        :theme-feed-type="themeFeedType"
        :doc-type="docType"
        :theme="theme"
        :enable-to-edit-theme="enableToEditTheme"
        >{{ noFeedMessage }}</ThemeEmptyFeed
      >
      <BasicEmptyFeed v-else>{{ noFeedMessage }}</BasicEmptyFeed>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.document-list {
  background: #fff;
  border-radius: 4px;

  .title {
    display: flex;
    align-items: center;
    padding-left: 16px;
    border-radius: 4px;
    background: $color-white;

    .title-text {
      padding-top: 10px;
      padding-bottom: 10px;
    }
    .feed-count {
      color: $color-gray600;
    }
  }

  .divider {
    width: auto;
    border-bottom: 0.5px solid $color-gray400;
    margin: 0px 16px 0px 16px;
  }

  .document-card-wrapper {
    margin-bottom: 8px;
    border-radius: 4px;

    .document-card {
      border: 0;
    }
  }

  .view-more-button__wrapper {
    text-align: center;
  }
  .view-more-button {
    display: inline-flex;
    align-self: center;
    margin-bottom: 16px;
    padding: 4px 12px;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
  }
}
</style>
